import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";

// Tracked for quoting vs the storefront, and retailer brand names (USA-1105).
type ManufacturerDetail = components["schemas"]["ManufacturerDetailRead"];
export type ManufacturerAlias = components["schemas"]["ManufacturerAliasRead"];

export function useUpdateQuoteTracking(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async (quoteTracking: boolean) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/manufacturers/{uuid}/quote-tracking",
        {
          params: { path: { uuid: manufacturerUuid } },
          body: { quote_tracking: quoteTracking },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (quoteTracking) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old ? { ...old, quote_tracking: quoteTracking } : old,
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manufacturers.all,
      });
    },
  });
}

/** Priced on each quote: the manufacturer's products have no catalog price (USA-1107). */
export function useUpdateCustomPricing(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async (customPricing: boolean) => {
      const client = await getClient();
      const { data, error } = await client.PATCH("/dashboard/manufacturers/{uuid}", {
        params: { path: { uuid: manufacturerUuid } },
        body: { custom_pricing: customPricing },
      });
      if (error) throw error;
      return data;
    },
    onMutate: async (customPricing) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old ? { ...old, custom_pricing: customPricing } : old,
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });
}

export function useAddManufacturerAlias(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async (alias: string) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/aliases",
        { params: { path: { uuid: manufacturerUuid } }, body: { alias } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old
          ? {
              ...old,
              aliases: [...(old.aliases ?? []), created].sort((a, b) =>
                a.alias.localeCompare(b.alias),
              ),
            }
          : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });
}

export function useRemoveManufacturerAlias(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async (aliasId: number) => {
      const client = await getClient();
      const { error } = await client.DELETE(
        "/dashboard/manufacturers/{uuid}/aliases/{alias_id}",
        { params: { path: { uuid: manufacturerUuid, alias_id: aliasId } } },
      );
      if (error) throw error;
    },
    onMutate: async (aliasId) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old
          ? {
              ...old,
              aliases: old.aliases?.filter((alias) => alias.id !== aliasId),
            }
          : old,
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });
}

export type ManufacturerAddress = components["schemas"]["ManufacturerAddressUpdate"];

/** Edit the address; an edited field wins over AutoQuotes from then on (USA-1105). */
export function useUpdateManufacturerAddress(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (address: ManufacturerAddress) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/manufacturers/{uuid}/address",
        { params: { path: { uuid: manufacturerUuid } }, body: address },
      );
      if (error) throw error;
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manufacturers.detail(manufacturerUuid),
      });
    },
  });
}

/** Retire a manufacturer we can no longer sell: its products are discontinued in the background (USA-1445). */
export function useRetireManufacturer(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async () => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/retire",
        { params: { path: { uuid: manufacturerUuid } } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (retirement) => {
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old
          ? { ...old, retirement, aq_tracking: false, quote_tracking: false }
          : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}

/** Hide a manufacturer's brand page and products on the store, or show them again (USA-1460). */
export function useSetManufacturerStoreHidden(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manufacturerUuid);
  return useMutation({
    mutationFn: async (hidden: boolean) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/manufacturers/{uuid}/store-visibility",
        { params: { path: { uuid: manufacturerUuid } }, body: { hidden } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (visibility) => {
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) =>
        old ? { ...old, ...visibility } : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}
